import { isRTL } from "@core/shared/utils";
import { cn } from "../../utils/helpers";
import { type TabProps } from "./types";

type TabsListProps = {
  tabs: React.ReactElement<TabProps>[];
  activeTab: string;
  orientation: "horizontal" | "vertical";
  onTabChange: (value: string) => void;
  baseId?: string;
};

export function TabsList({
  tabs,
  activeTab,
  orientation,
  onTabChange,
  baseId,
}: TabsListProps) {
  const isRTLValue = isRTL();

  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      className={cn(
        "flex",
        orientation === "vertical"
          ? [
              "flex-col",
              isRTLValue
                ? "border-s dark:border-gray-700"
                : "border-e dark:border-gray-700",
            ]
          : "flex-row border-b dark:border-gray-700",
        "gap-2",
      )}>
      {tabs.map(tab => {
        const isActive = tab.props.value === activeTab;
        const tabId =
          tab.props.id || baseId
            ? `${baseId}-tab-${tab.props.value}`
            : tab.props.value;
        const panelId = baseId
          ? `${baseId}-panel-${tab.props.value}`
          : `${tab.props.value}-panel`;

        return (
          <button
            key={tabId}
            id={tabId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            aria-disabled={tab.props.disabled}
            tabIndex={isActive ? 0 : -1}
            onClick={() => !tab.props.disabled && onTabChange(tab.props.value)}
            disabled={tab.props.disabled}
            className={cn(
              "relative flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200",
              "outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
              "active:scale-95 hover:bg-gray-50/50 dark:hover:bg-gray-800/50",
              orientation === "vertical"
                ? isRTLValue
                  ? "border-s-2"
                  : "border-e-2"
                : [
                    "border-b-2",
                    "after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-full after:scale-x-0 after:bg-primary-500 after:transition-transform after:duration-200",
                    isActive && "after:scale-x-100",
                  ],
              isActive
                ? "border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/20"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
              tab.props.disabled &&
                "cursor-not-allowed opacity-50 pointer-events-none",
            )}>
            {isRTLValue ? (
              <>
                {tab.props.label}
                {tab.props.icon && (
                  <span className="h-4 w-4 ms-2" aria-hidden="true">
                    {tab.props.icon}
                  </span>
                )}
              </>
            ) : (
              <>
                {tab.props.icon && (
                  <span className="h-4 w-4 me-2" aria-hidden="true">
                    {tab.props.icon}
                  </span>
                )}
                {tab.props.label}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
